import React, { useEffect, useMemo, useState } from 'react';
import { BsShareFill } from 'react-icons/bs';
import { getButtonText, getCardAction } from '../utils';
import * as nativeEvents from '../../../Helper/native';
import * as APIHelper from '../../../Helper/apiFetch';
import { ASSET_URLS, BUTTON_SPINNER_STYLE } from '../constants';
import { getTodayDateString } from '../utils';
import ValentinesBrands from './ValentinesBrands';

const initiaData = require('../../urlEncodeTest.json');

// Default Valentine-themed gradient colors for fallback
const DEFAULT_BG_COLORS = [
  'linear-gradient(180deg, #E91E63 0%, #C2185B 50%, #880E4F 100%)',
  'linear-gradient(180deg, #F48FB1 0%, #E91E63 50%, #C2185B 100%)',
  'linear-gradient(180deg, #EC407A 0%, #E91E63 50%, #AD1457 100%)',
  'linear-gradient(180deg, #F8BBD0 0%, #F48FB1 50%, #E91E63 100%)',
];

// Mock data for when API has no data
const MOCK_BRAND_DATA = {
  error: false,
  message: 'Success',
  data: {
    date: '2026-02-10',
    voucher: {
      id: 'GSGC3461',
      icon: 'https://assets.gosats.io/merchant/logo/png/cleartrip0922.png',
      bgColor: '#FFFFFF',
      name: 'Cleartrip',
    },
  },
};

const ValentinesBanner = ({ cardType, cardPaymentStatus, onShare, onBrandScreenChange }) => {
  const [showBrands, setShowBrands] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [brandsData, setBrandsData] = useState(null);

  const valentineTarget = useMemo(() => {
    const now = new Date();
    // Feb is month index 1; countdown ends on Feb 14th, 23:59:59 (local time)
    return new Date(now.getFullYear(), 1, 14, 23, 59, 59);
  }, []);

  const [secondsLeft, setSecondsLeft] = useState(() => {
    const diffMs = valentineTarget - new Date();
    return Math.max(0, Math.floor(diffMs / 1000));
  });

  useEffect(() => {
    const id = setInterval(() => {
      const diffMs = valentineTarget - new Date();
      setSecondsLeft(Math.max(0, Math.floor(diffMs / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [valentineTarget]);

  // Add spinner animation keyframes
  useEffect(() => {
    const spinKeyframes = `
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `;
    const styleSheet = document.createElement('style');
    styleSheet.innerText = spinKeyframes;
    document.head.appendChild(styleSheet);
    return () => {
      if (document.head.contains(styleSheet)) {
        document.head.removeChild(styleSheet);
      }
    };
  }, []);

  const timerText = useMemo(() => {
    const ONE_DAY_SECONDS = 24 * 60 * 60;

    if (secondsLeft >= ONE_DAY_SECONDS) {
      const daysLeft = Math.ceil(secondsLeft / ONE_DAY_SECONDS);
      if (daysLeft > 1) return `${daysLeft} days left`;
      return '24hrs';
    }

    const totalHours = Math.floor(secondsLeft / 3600);
    const minutes = Math.floor((secondsLeft % 3600) / 60);
    const seconds = secondsLeft % 60;
    return `${String(totalHours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}:${String(seconds).padStart(2, '0')}`;
  }, [secondsLeft]);

  const handleCardAction = () => {
    const action = getCardAction(cardType, cardPaymentStatus);
    nativeEvents[action]();
  };

  const handleRevealSurprise = async () => {
    try {
      setLoadingBrands(true);
      const response = await APIHelper.revealDailyDeals();

      // Use mock data if API has error or no data
      let responseToUse = response;
      if (response.error || !response.data || (!response.data.voucher && !response.data.brands && !response.data.voucherOfDay)) {
        console.log('Using mock data - API has no data');
        responseToUse = MOCK_BRAND_DATA;
      }

      // Map API response to brand format
      let brandsDataArray = [];

      if (responseToUse.data) {
        // Check for voucher structure (new API format)
        if (responseToUse.data.voucher) {
          brandsDataArray = responseToUse.data.voucher;
        }
        // Check if data has brands array
        else if (Array.isArray(responseToUse.data.brands)) {
          brandsDataArray = responseToUse.data.brands;
        } else if (Array.isArray(responseToUse.data)) {
          brandsDataArray = responseToUse.data;
        } else if (responseToUse.data.voucherOfDay) {
          // Single voucher format - convert to array
          brandsDataArray = [responseToUse.data.voucherOfDay];
        } else if (responseToUse.data[getTodayDateString()]) {
          // Date-based format
          brandsDataArray = [responseToUse.data[getTodayDateString()]];
        } else if (responseToUse.brands && Array.isArray(responseToUse.brands)) {
          brandsDataArray = responseToUse.brands;
        }
      } else if (Array.isArray(responseToUse)) {
        brandsDataArray = responseToUse;
      }

      // Map to brand card format
      const mappedBrands = brandsDataArray.map((brand, index) => ({
        imgUrl: brand.icon || brand.imgUrl || brand.image || '',
        bgColor:
          brand.bgColor
            ? (brand.bgColor.startsWith('linear-gradient') || brand.bgColor.startsWith('#') || brand.bgColor.startsWith('rgb')
                ? brand.bgColor
                : `linear-gradient(180deg, ${brand.bgColor} 0%, ${brand.bgColor} 100%)`)
            : (brand.background
                ? `linear-gradient(180deg, ${brand.background} 0%, ${brand.background} 100%)`
                : DEFAULT_BG_COLORS[index % DEFAULT_BG_COLORS.length]),
        flatReward:
          brand.flatReward ||
          brand.reward ||
          brand.rewardText ||
          `Extra ${brand.rewardPercent || 5}% Reward`,
        borderColor: brand.borderColor || '1px solid #FFFFFF33',
        merchantId:
          brand.merchantId ||
          brand.merchant_id ||
          brand.id ||
          '',
      }));

      const filteredBrands = mappedBrands.filter((brand) => brand.merchantId && brand.imgUrl);
      setBrandsData(filteredBrands);
      setShowBrands(true);
      if (onBrandScreenChange) {
        onBrandScreenChange(true);
      }
    } catch (err) {
      console.error('Error fetching brands:', err);
      // Use mock data on error
      const mockVoucher = MOCK_BRAND_DATA.data.voucher;
      const mappedBrand = {
        imgUrl: mockVoucher.icon,
        bgColor: mockVoucher.bgColor.startsWith('#') 
          ? `linear-gradient(180deg, ${mockVoucher.bgColor} 0%, ${mockVoucher.bgColor} 100%)`
          : mockVoucher.bgColor,
        flatReward: 'Extra 5% Reward',
        borderColor: '1px solid #FFFFFF33',
        merchantId: mockVoucher.id,
      };
      setBrandsData([mappedBrand]);
      setShowBrands(true);
      if (onBrandScreenChange) {
        onBrandScreenChange(true);
      }
    } finally {
      setLoadingBrands(false);
    }
  };

  // Show brands screen if revealed
  if (showBrands) {
    return (
      <ValentinesBrands
        brands={brandsData}
        onBack={() => {
          setShowBrands(false);
          if (onBrandScreenChange) {
            onBrandScreenChange(false);
          }
        }}
      />
    );
  }

  return (
    <div className="valentines-banner">
      <div className="valentines-headerCta">
        <div className="valentines-timer"> 
          <div className="valentines-timer-text"> 
            <img alt=' ' className='timerIcon' src={'https://assets.gosats.io/notification/2026/2/1770707216611'} /> 
            {timerText}
          </div> 
        </div>
        <button
          className="valentines-shareCta"
          onClick={() => onShare(initiaData.floatingShareMsg.msg)}
        >
          <BsShareFill size={22} color="#FFFFFF" />
        </button>
      </div>
      <div style={{ position: 'relative', marginTop: '30px', marginBottom: '-30px' }}>
        <div className="valentines-title">
        This Valentine’s Week <br />
        {/* <span className="valentines-subtitle">Stack Rewards.</span> */}
        </div>
        <div className="valentinesSubtitle">
            Earn <span className="valentines-highlight"> Boosted Rewards </span> every day across your favourite brands & upto <span className="valentines-highlight"> 3% Rewards </span> everytime you spend with GoSats Elite Card.
        </div>
        <img src="https://assets.gosats.io/notification/2026/2/1770703897315" alt="" className="valentines-image"/>
      </div>
      <div className="valentines-ctaButtonContainer">
        <button
          className="valentines-ctaButton"
          onClick={handleRevealSurprise}
          disabled={loadingBrands}
        >
          {loadingBrands ? (
            <div style={BUTTON_SPINNER_STYLE}></div>
          ) : (
            "REVEAL TODAY'S SURPRISE"
          )}
        </button>
      </div>
    </div>
  );
};

export default ValentinesBanner;
